import { DeleteOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Avatar, Button, Input, Modal, Form, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePagination } from "@/shared/hooks/usePagination";
import { artistService } from "../services/artist.service";

const { Title } = Typography;

export function ArtistsPage() {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const { page, pageSize, onChange } = usePagination();
	const [search, setSearch] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [form] = Form.useForm();

	const { data, isLoading } = useQuery({
		queryKey: ["artists", { page, per_page: pageSize, search }],
		queryFn: () => artistService.list({ page, per_page: pageSize, search: search || undefined }),
	});

	const createMutation = useMutation({
		mutationFn: (v: Record<string, unknown>) => artistService.create(v),
		onSuccess: (res) => {
			message.success("Sanatkor qo'shildi");
			setModalOpen(false);
			form.resetFields();
			queryClient.invalidateQueries({ queryKey: ["artists"] });
			navigate(`/artists/${res.data.id}`);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => artistService.remove(id),
		onSuccess: () => { message.success("O'chirildi"); queryClient.invalidateQueries({ queryKey: ["artists"] }); },
	});

	const artists = data?.data?.artists || [];
	const pagination = data?.data?.pagination;

	const columns = [
		{
			title: "Rasm", dataIndex: "image", width: 60,
			render: (v: string) => <Avatar src={v} icon={!v && <span>?</span>} />,
		},
		{ title: "Ism", dataIndex: "name" },
		{ title: "Sahna ismi", dataIndex: "stage_name" },
		{ title: "Janr", dataIndex: "genre" },
		{ title: "Yuz rasmlar", dataIndex: "face_count", render: (v: number) => <Tag color={v > 0 ? "green" : "red"}>{v}</Tag> },
		{
			title: "Amallar", width: 120,
			render: (_: unknown, r: { id: number }) => (
				<Space>
					<Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/artists/${r.id}`)} />
					<Popconfirm title="O'chirish?" onConfirm={() => deleteMutation.mutate(r.id)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">Sanatkorlar</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>Qo'shish</Button>
			</div>
			<Input placeholder="Qidiruv..." prefix={<SearchOutlined />} className="mb-4 max-w-xs" value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
			<Table columns={columns} dataSource={artists} rowKey="id" loading={isLoading}
				pagination={{ current: pagination?.current_page, pageSize: pagination?.per_page, total: pagination?.total_items, onChange, showSizeChanger: true }} />
			<Modal title="Yangi sanatkor" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} confirmLoading={createMutation.isPending}>
				<Form form={form} layout="vertical" onFinish={(v) => createMutation.mutate(v)}>
					<Form.Item name="name" label="To'liq ism" rules={[{ required: true }]}><Input /></Form.Item>
					<Form.Item name="stage_name" label="Sahna ismi"><Input /></Form.Item>
					<Form.Item name="genre" label="Janr"><Input placeholder="pop, xalq, estrada..." /></Form.Item>
					<Form.Item name="phone" label="Telefon"><Input /></Form.Item>
				</Form>
			</Modal>
		</div>
	);
}
